import React from 'react';
import { X, Minus, Plus, Lock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useCurrency } from '../context/CurrencyContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';

const CartSheet = () => {
  const {
    cart, cartOpen, setCartOpen, removeFromCart, updateQty, clearCart,
    subtotal, shipping, total, openCheckout,
  } = useApp();
  const { fmt, fmtLocal, totals } = useCurrency();
  const loc = totals(cart, shipping);
  const { isAuthed, openAuth } = useCustomerAuth();

  if (!cartOpen) return null;

  const itemCount = cart.reduce((s, i) => s + i.qty, 0);

  const handleCheckout = () => {
    if (!isAuthed) {
      // Ask user to sign in first; auto-continue to checkout after successful auth
      setCartOpen(false);
      openAuth(() => {
        // Small delay so state updates settle
        setTimeout(() => { openCheckout(); }, 150);
      }, 'Please sign in or create an account to place your order.');
      return;
    }
    openCheckout();
  };

  return (
    <div className="fixed inset-0 z-50" onClick={() => setCartOpen(false)}>
      <div className="absolute inset-0 bg-black/40" />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-white dark:bg-[#141416] shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-200 dark:border-neutral-800">
          <h3 className="font-serif text-[24px] text-neutral-900 dark:text-neutral-100">Cart</h3>
          <button onClick={() => setCartOpen(false)} className="p-1.5 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800"><X size={18} /></button>
        </div>

        {cart.length > 0 && (
          <div className="flex items-center justify-between px-6 pt-4 pb-2 text-[13px]">
            <span className="text-neutral-500 dark:text-neutral-400">{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
            <button onClick={clearCart} className="text-neutral-500 hover:text-red-500 transition-colors">Clear all</button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 pt-2 pb-4">
          {cart.length === 0 && (
            <div className="text-center py-20 text-neutral-500">
              <p className="text-[14px]">Your cart is empty.</p>
              <button onClick={() => setCartOpen(false)} className="mt-4 text-[13px] underline">Continue shopping</button>
            </div>
          )}

          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {cart.map(item => (
              <div key={item.id} className="flex items-start gap-3 py-4">
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-medium text-neutral-900 dark:text-neutral-100 truncate">{item.name}</div>
                  <div className="text-[13px] text-neutral-500 dark:text-neutral-400 mt-0.5">{item.strength}{item.packaging ? ' - ' + item.packaging : ''}</div>
                  <div className="text-[12.5px] text-neutral-500 mt-1">{fmt(item.price)} each</div>
                </div>
                <div className="flex items-center border border-neutral-200 dark:border-neutral-700 rounded-full">
                  <button onClick={() => updateQty(item.id, item.qty - 1)} className="p-1.5 pl-3 text-neutral-600 dark:text-neutral-300 hover:text-neutral-900" aria-label="decrease"><Minus size={13} /></button>
                  <span className="px-2 text-[13.5px] min-w-[24px] text-center">{item.qty}</span>
                  <button onClick={() => updateQty(item.id, item.qty + 1)} className="p-1.5 pr-3 text-neutral-600 dark:text-neutral-300 hover:text-neutral-900" aria-label="increase"><Plus size={13} /></button>
                </div>
                <button onClick={() => removeFromCart(item.id)} className="text-neutral-300 hover:text-red-500 p-1" aria-label="remove"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        {cart.length > 0 && (
          <div className="border-t border-neutral-200 dark:border-neutral-800 px-6 py-5 space-y-3">
            <div className="flex justify-between text-[14px]">
              <span className="text-neutral-800 dark:text-neutral-200">Subtotal</span>
              <span className="text-neutral-900 dark:text-neutral-100 font-medium">{fmtLocal(loc.subtotal)}</span>
            </div>
            <div className="flex justify-between text-[14px]">
              <span className="text-neutral-800 dark:text-neutral-200">Shipping</span>
              <span className="text-neutral-900 dark:text-neutral-100 font-medium">{fmtLocal(loc.shipping)}</span>
            </div>

            <div className="flex justify-between items-baseline pt-2 border-t border-neutral-200 dark:border-neutral-800">
              <span className="text-[15px] text-neutral-900 dark:text-neutral-100">Total</span>
              <span className="text-[20px] font-semibold text-neutral-900 dark:text-neutral-100">{fmtLocal(loc.total)}</span>
            </div>

            {!isAuthed && (
              <div className="flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 dark:bg-amber-950/30 dark:border-amber-900 p-3 text-[12px] text-amber-800 dark:text-amber-200">
                <Lock size={13} className="mt-0.5 shrink-0" />
                <span>Sign in required to place an order. You'll be prompted at checkout.</span>
              </div>
            )}

            <button data-testid="cart-checkout" onClick={handleCheckout} className="btn-brand w-full rounded-lg py-3.5 text-[14.5px] font-semibold tracking-wide">
              {isAuthed ? `Checkout — ${fmtLocal(loc.total)}` : `Sign in to checkout`}
            </button>
          </div>
        )}
      </aside>
    </div>
  );
};

export default CartSheet;
